import { FC, useCallback, useMemo } from 'react';
import { FieldErrors, useForm } from 'react-hook-form';
import { Separator } from '@/components/elements/Seperator';
import Container from '@/components/global/Container';
import { Sticky } from '@/components/global/Sticky';
import { determineLocaleCountry } from '@/helpers/determineLocale';
import { generateHalfHourIncrements } from '@/helpers/generateTimeIncrements';
import classNames from '@/lib/classNames';
import { jucyAttribution } from '@/lib/jucyAttribution';
import { useObeUrl } from '@/lib/useObeUrl';
import { zodResolver } from '@hookform/resolvers/zod';
import { Site, SiteServiceHours, SiteSiteSettings } from '@jucy/rentals-api-client';
import { format, parseISO } from 'date-fns';
import { useRouter } from 'next/router';
import _data from 'public/sites.json';
import { z } from 'zod';
import Button from '../../elements/Button';
import { Form } from '../../elements/Form';
import { SelectGroup, SelectItem, SelectLabel, SelectSeparator } from '../../elements/Select/Select';
import { BookingSearchSelectItem } from './BookingSearchItem';
import DateRangePicker from './DateRangePicker';

const BookingSearchFormSchema = z.object({
    pickUpLocation: z.string({
        required_error: 'Pick up location required',
    }),
    pickUpDate: z.date({ required_error: 'Pick up date required' }).min(new Date(), 'Pick up time cannot be in the past'),
    pickUpTime: z.string({ required_error: 'Pick up time required' }),
    dropOffLocation: z.string({
        required_error: 'Drop off location required',
    }),
    dropOffDate: z.date({ required_error: 'Drop off date required' }),
    dropOffTime: z.string({ required_error: 'Drop off time required' }),
    promoCode: z.string().optional(),
});

export type BookingSearchFormData = z.infer<typeof BookingSearchFormSchema>;

interface CountryOptionsGroupProps {
    country: string;
    sites: Site[];
}

const CountryOptionsGroup: FC<CountryOptionsGroupProps> = ({ country, sites }) => {
    if (!sites?.length) {
        return null;
    }

    return (
        <SelectGroup key={country}>
            <SelectLabel className="h6 text-primary py-2 px-2" key={country}>
                {country}
            </SelectLabel>
            {sites.map((site, index) => (
                <SelectItem key={site.id ?? `id-${index}`} value={site.siteCode ?? ''}>
                    {site.name}
                </SelectItem>
            ))}
        </SelectGroup>
    );
};

const BookingSearchItemGroup = ({
    form,
    errors,
    sortedSitesByCountry,
    getAvailableDropOffTimes,
    isPickUp,
}: {
    form: ReturnType<typeof useForm>;
    errors: FieldErrors;
    sortedSitesByCountry: {
        [x: string]: Site[];
    };
    getAvailableDropOffTimes: (pickUp: boolean) => string[];
    isPickUp: boolean;
}) => {
    const labelPrefix = isPickUp ? 'Pick Up' : 'Drop Off';
    const namePrefix = isPickUp ? 'pickUp' : 'dropOff';

    return (
        <div className="relative flex flex-col lg:flex-row lg:space-x-4 items-center justify-between align-center p-2 border box-border border-primary rounded py-2 lg:pb-0.5 lg:py-1 xl:py-2">
            <BookingSearchSelectItem className="flex items-center lg:w-1/3" name={`${namePrefix}Location`} ariaLabel={`${labelPrefix} Location`} hasError={!!errors[`${namePrefix}Location`]}>
                {isPickUp
                    ? Object.entries(sortedSitesByCountry).map(([country, sites]) => <CountryOptionsGroup country={country} key={country} sites={sites} />)
                    : Object.entries(sortedSitesByCountry).map(([country, sites]) => {
                          // Only select from countries in pickUpLocation
                          const pickupLocation = form.watch('pickUpLocation');
                          return pickupLocation ? (
                              sites.map((site) => site.siteCode).includes(pickupLocation) ? (
                                  <CountryOptionsGroup country={country} key={country} sites={sites} />
                              ) : null
                          ) : (
                              <CountryOptionsGroup country={country} key={country} sites={sites} />
                          );
                      })}
            </BookingSearchSelectItem>
            <SelectSeparator className="mb-2 lg:hidden" />
            <Separator className="hidden xl:block w-[2px] h-4" />
            <div className="relative flex flex-row items-center space-x-6 xl:space-x-4 2xl:space-x-6 w-full lg:w-2/3">
                <div className="w-full lg:w-1/2">
                    <DateRangePicker name={`${namePrefix}Date`} ariaLabel={`${labelPrefix} Date`} hasError={!!errors[`${namePrefix}Date`]} />
                </div>
                <BookingSearchSelectItem className="flex items-center" name={`${namePrefix}Time`} ariaLabel={`${labelPrefix} Time`} hasError={!!errors[`${namePrefix}Time`]}>
                    {getAvailableDropOffTimes(isPickUp).map((time, index) => (
                        <SelectItem key={`${namePrefix}-time-${index}`} value={time}>
                            {time}
                        </SelectItem>
                    ))}
                </BookingSearchSelectItem>
            </div>
        </div>
    );
};

const ErrorMessage = ({ fieldName, errors }: { fieldName: string; errors: FieldErrors }) => {
    return String(errors[fieldName]?.message ?? '');
};

const BookingSearchItemGroupErrors = ({ errors, isPickUp }: { errors: FieldErrors; isPickUp: boolean }) => {
    const namePrefix = isPickUp ? 'pickUp' : 'dropOff';
    const fieldNames = [`${namePrefix}Location`, `${namePrefix}Date`, `${namePrefix}Time`];

    if (Object.keys(errors).length <= 0) {
        return null;
    }

    return (
        <div className="flex flex-col gap-1 px-2">
            {fieldNames.map(
                (fieldName) =>
                    errors[fieldName] && (
                        <p key={fieldName} className="text-[0.75rem] text-destructive">
                            <ErrorMessage fieldName={fieldName} errors={errors} />
                        </p>
                    )
            )}
        </div>
    );
};

const useDefaultFormValues = () => {
    const query = useRouter().query as Record<string, string>;
    const pickUpDate = z.date().safeParse(query.pickUpDate);
    const dropOffDate = z.date().safeParse(query.dropOffDate);
    const result: Partial<BookingSearchFormData> = {
        pickUpLocation: query.pickUpLocation,
        pickUpDate: pickUpDate.success ? pickUpDate.data : undefined,
        pickUpTime: query.pickUpTime,
        dropOffLocation: query.dropOffLocation,
        dropOffDate: dropOffDate.success ? dropOffDate.data : undefined,
        dropOffTime: query.dropOffTime,
        promoCode: query.promoCode,
    };
    if (query.pickUpDateTime) {
        const pickUpDateTime = parseISO(query.pickUpDateTime);
        if (pickUpDateTime) {
            result.pickUpDate = pickUpDateTime;
            result.pickUpTime = format(pickUpDateTime, 'HHmm');
        }
    }
    if (query.dropOffDateTime) {
        const dropOffDateTime = parseISO(query.dropOffDateTime);
        if (dropOffDateTime) {
            result.dropOffDate = dropOffDateTime;
            result.dropOffTime = format(dropOffDateTime, 'HHmm');
        }
    }
    const data = BookingSearchFormSchema.partial().safeParse(result);
    return data.success ? data.data : undefined;
};

const BookingSearchBarComponent = ({ className, bookingBarText }: { className?: string; bookingBarText?: string }) => {
    const sites = _data as unknown as Site[];
    const router = useRouter();
    const obeUrl = useObeUrl();
    const localeCountry = determineLocaleCountry(router.locale);
    const defaultValues = useDefaultFormValues();
    const sitesByCountry = useMemo(
        () =>
            sites
                ? sites.reduce(
                      (countries, site) => {
                          const { country = 'Other' } = site;

                          countries[country] = countries[country] ?? [];
                          countries[country].push(site);

                          return countries;
                      },
                      {} as {
                          [country: string]: Array<Site>;
                      }
                  )
                : {},
        [sites]
    );

    const sortedSitesByCountry = useMemo(
        () => ({
            // Make the locale the top country in dropdown
            [localeCountry]: sitesByCountry[localeCountry],
            ...Object.fromEntries(Object.entries(sitesByCountry).filter(([key]) => key !== localeCountry)),
        }),
        [sitesByCountry, localeCountry]
    );

    const form = useForm<BookingSearchFormData>({
        resolver: zodResolver(
            BookingSearchFormSchema.superRefine((data, ctx) => {
                const dropOffSiteCode = data.dropOffLocation;
                const dropOffCountry = (dropOffSiteCode && sites?.find((s) => s.siteCode === dropOffSiteCode)?.country) || '';
                const pickUpSiteCode = data.pickUpLocation;
                const pickUpCountry = (pickUpSiteCode && sites?.find((s) => s.siteCode === pickUpSiteCode)?.country) || '';

                if (pickUpCountry !== dropOffCountry) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: 'Drop off location must be in the same country as pick up location',
                        path: ['dropOffLocation'],
                    });
                }
            })
        ),
        defaultValues,
    });

    const { errors } = form.formState;

    const onSubmit = useCallback(
        (data: BookingSearchFormData) => {
            const { pickUpLocation, pickUpDate, pickUpTime, dropOffLocation, dropOffTime, dropOffDate, promoCode } = data;
            const pickUpSites = sites.find((site) => site.siteCode === pickUpLocation);
            const formattedPickUpDate = format(pickUpDate, 'yyyy-MM-dd');
            const formattedDropOffDate = format(dropOffDate, 'yyyy-MM-dd');
            const url = pickUpSites?.countryCode ? new URL(`/obe/${pickUpSites.countryCode}/direct/trip/create/motorhome`, obeUrl) : obeUrl;
            url.searchParams.set('pickUpLocation', pickUpLocation);
            url.searchParams.set('dropOffLocation', dropOffLocation);
            url.searchParams.set('pickUpDate', formattedPickUpDate);
            url.searchParams.set('dropOffDate', formattedDropOffDate);
            url.searchParams.set('pickUpTime', pickUpTime);
            url.searchParams.set('dropOffTime', dropOffTime);
            if (promoCode) {
                url.searchParams.set('promoCode', promoCode);
            }
            url.searchParams.set('action', 'search');
            router.push(jucyAttribution.decorateUrl(url.href));
        },
        [obeUrl, router, sites]
    );

    const getAvailableDropOffTimes = useMemo(
        () => (pickUp: boolean) => {
            const pickupDateDayOfWeek =
                form.watch(pickUp ? 'pickUpDate' : 'dropOffDate') &&
                form
                    .watch(pickUp ? 'pickUpDate' : 'dropOffDate')
                    .toLocaleString('en-us', { weekday: 'long' })
                    .toLowerCase();

            const locationInformation = sites?.find((item: Site) => item.siteCode === form.watch('pickUpLocation'));

            const pickupTimesOpenAndClose =
                (form.watch(pickUp ? 'pickUpDate' : 'dropOffDate') &&
                    form.watch(pickUp ? 'pickUpLocation' : 'dropOffLocation') &&
                    pickupDateDayOfWeek &&
                    locationInformation &&
                    locationInformation?.siteSettings?.find((item: SiteSiteSettings) => item?.fleetTypeId === '7f6a1e49-d686-4e0b-8302-d58d26606420')?.serviceHours?.[
                        (pickupDateDayOfWeek as keyof SiteServiceHours) || 'monday'
                    ]) ||
                null;

            return generateHalfHourIncrements(pickupTimesOpenAndClose?.open, pickupTimesOpenAndClose?.close);
        },
        [form, sites]
    );

    const FormFields = (
        <div className="flex flex-col lg:flex-row lg:py-2 xl:py-6 lg:px-4 lg:space-x-4 space-y-6 lg:space-y-0 w-full bg-white rounded-3xl transition duration-300 lg:shadow-lg group-[.pinned]:shadow-2xl group-[.pinned]:rounded-none">
            <div className="flex flex-col lg:flex-row space-y-4 lg:space-x-4 lg:space-y-0 w-full lg:max-w-[82.5%] xl:max-w-[80%]">
                <div className="flex flex-col w-full lg:max-w-[calc(50%_-_8px)] xl:max-w-none lg:justify-center xl:justify-normal">
                    <BookingSearchItemGroup
                        form={form as unknown as ReturnType<typeof useForm>}
                        errors={errors}
                        sortedSitesByCountry={sortedSitesByCountry}
                        getAvailableDropOffTimes={getAvailableDropOffTimes}
                        isPickUp={true}
                    />
                    <BookingSearchItemGroupErrors errors={errors} isPickUp={true} />
                </div>
                <div className="flex flex-col w-full lg:max-w-[calc(50%_-_8px)] xl:max-w-none lg:justify-center xl:justify-normal">
                    <BookingSearchItemGroup
                        form={form as unknown as ReturnType<typeof useForm>}
                        errors={errors}
                        sortedSitesByCountry={sortedSitesByCountry}
                        getAvailableDropOffTimes={getAvailableDropOffTimes}
                        isPickUp={false}
                    />
                    <BookingSearchItemGroupErrors errors={errors} isPickUp={false} />
                </div>
            </div>

            <div className="flex items-center w-full lg:max-w-[calc(17.5%_-_16px)] xl:max-w-[calc(20%_-_16px)]">
                <Button variant="fill" type="submit" size="lg" uppercase className="w-full">
                    Search
                </Button>
            </div>
        </div>
    );

    return (
        <div className={classNames(className)}>
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)}>
                    <Container>
                        {bookingBarText && <p className="h6 lg:text-white mb-2">{bookingBarText}</p>}

                        <Sticky id="booking" start={16} target="body" className="group z-20 lg:mb-32">
                            <div className="group-[.pinned]:bg-white">{FormFields}</div>
                        </Sticky>
                    </Container>
                </form>
            </Form>
        </div>
    );
};
const BookingSearchBar = ({ className, bookingBarText }: { className?: string; bookingBarText?: string }) => {
    const router = useRouter();
    return <BookingSearchBarComponent key={router.isReady ? '1' : '2'} className={className} bookingBarText={bookingBarText} />;
};
export default BookingSearchBar;
